import { useEffect, useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { MessageSquare, Send, Search, ArrowLeft, Plus, Loader2, Trash2, Paperclip, X, FileText, Film, Image as ImageIcon, Clock, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { uploadMessageAttachment, attachmentUrl, formatSize } from "../lib/messageAttachments";

const ACCEPT = "image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip";

const ROLE_COLORS = { admin: "#f7c935", coach: "#3b82f6", sous_traitant: "#10b981", client: "#8b5cf6" };
const ROLE_LABELS = { admin: "Admin", coach: "Coach", sous_traitant: "Sous-traitant", client: "Client" };

export default function MessagesPage() {
  const { user, axiosAuth } = useAuth();
  const api = axiosAuth();
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [files, setFiles] = useState([]);
  const [upload, setUpload] = useState(null);
  const fileRef = useRef(null);

  // New conversation dialog
  const [showNew, setShowNew] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState("");

  const loadConversations = async () => {
    try {
      const r = await api.get("/messages/conversations");
      setConversations(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadMessages = async (convoId) => {
    try {
      const r = await api.get(`/messages/${convoId}`);
      setMessages(r.data);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    loadConversations();
    const id = setInterval(loadConversations, 10000);
    return () => { clearInterval(id); if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (activeConvo) {
      loadMessages(activeConvo.conversation_id);
      pollRef.current = setInterval(() => {
        if (document.visibilityState === "visible" && navigator.onLine) loadMessages(activeConvo.conversation_id);
      }, 3000);
    }
    setFiles([]);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeConvo?.conversation_id]);

  const pickFiles = (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length) setFiles((prev) => [...prev, ...list].slice(0, 10));
    e.target.value = "";
  };

  const sendMessage = async () => {
    if ((!newMsg.trim() && files.length === 0) || !activeConvo || sending) return;
    if (files.length && !navigator.onLine) { toast.error("Envoi de fichiers impossible hors ligne — le texte seul peut être envoyé"); return; }
    setSending(true);
    try {
      const attachment_ids = [];
      for (let i = 0; i < files.length; i += 1) {
        setUpload({ index: i + 1, total: files.length, progress: 0, name: files[i].name });
        const att = await uploadMessageAttachment(api, files[i], (p) => setUpload((u) => (u ? { ...u, progress: p } : u)));
        attachment_ids.push(att.attachment_id);
      }
      setUpload(null);
      await api.post(`/messages/${activeConvo.conversation_id}`, { content: newMsg, attachment_ids });
      setNewMsg("");
      setFiles([]);
      await loadMessages(activeConvo.conversation_id);
      await loadConversations();
    } catch (e) {
      setUpload(null);
      toast.error(e.response?.data?.detail || "Échec de l'envoi");
    }
    setSending(false);
  };

  const openNewConvo = async () => {
    try {
      const r = await api.get("/messages/contacts/list");
      setContacts(r.data);
    } catch (e) { console.error(e); }
    setContactSearch("");
    setShowNew(true);
  };

  const startConvo = async (targetId) => {
    try {
      const r = await api.post("/messages/conversations", { target_user_id: targetId });
      setShowNew(false);
      await loadConversations();
      setActiveConvo(r.data);
    } catch (e) { console.error(e); }
  };

  const getOtherName = (convo) => {
    const other = convo.other_participants?.[0];
    return other?.name?.split(" ")[0] || other?.email || "Inconnu";
  };

  const getOtherFull = (convo) => convo.other_participants?.[0] || {};

  const isStaff = user?.role && ["admin", "coach", "sous_traitant", "comptable"].includes(user.role);

  const deleteMessage = async (convoId, msgId) => {
    try {
      await api.delete(`/messages/${convoId}/${msgId}`);
      setMessages(prev => prev.filter(m => m.message_id !== msgId));
    } catch (e) { console.error(e); }
  };

  const deleteConversation = async (convoId, e) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer toute cette conversation ?")) return;
    try {
      await api.delete(`/messages/conversations/${convoId}`);
      setConversations(prev => prev.filter(c => c.conversation_id !== convoId));
      if (activeConvo?.conversation_id === convoId) {
        setActiveConvo(null);
        setMessages([]);
      }
    } catch (err) { console.error(err); }
  };

  const filteredConvos = conversations.filter(c => {
    if (!search) return true;
    const other = getOtherFull(c);
    return (other.name || "").toLowerCase().includes(search.toLowerCase()) ||
           (other.email || "").toLowerCase().includes(search.toLowerCase());
  });

  const filteredContacts = contacts.filter(c => {
    if (!contactSearch) return true;
    return (c.name || "").toLowerCase().includes(contactSearch.toLowerCase()) ||
           (c.email || "").toLowerCase().includes(contactSearch.toLowerCase());
  });

  if (loading) return <div className="p-8 text-slate-400">Chargement...</div>;

  return (
    <div className="flex h-full overflow-hidden" data-testid="messages-page">
      {/* Sidebar */}
      <div className={`${activeConvo ? "hidden md:flex" : "flex"} flex-col w-full md:w-96 border-r border-white/10 bg-[#161020] h-full`}>
        <div className="p-4 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-black"><span style={{ color: "#f7c935" }}>Messages</span></h2>
            <Button onClick={openNewConvo} size="sm" className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825]" data-testid="new-convo-btn">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-10 bg-[#0b0811] border-white/10 text-white placeholder:text-slate-500 focus:border-[#f7c935] h-9 text-sm" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredConvos.length === 0 ? (
            <div className="p-8 text-center">
              <MessageSquare className="w-10 h-10 mx-auto mb-3 text-slate-600" />
              <p className="text-slate-500 text-sm">Aucune conversation</p>
              <Button onClick={openNewConvo} variant="link" className="text-[#f7c935] text-sm mt-2">Demarrer une conversation</Button>
            </div>
          ) : filteredConvos.map(c => {
            const other = getOtherFull(c);
            const isActive = activeConvo?.conversation_id === c.conversation_id;
            return (
              <button key={c.conversation_id} onClick={() => setActiveConvo(c)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors border-b border-white/5 group ${isActive ? "bg-[#f7c935]/10 border-l-2 border-l-[#f7c935]" : "hover:bg-white/5"}`}
                data-testid={`convo-${c.conversation_id}`}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0" style={{ background: `${ROLE_COLORS[other.role] || "#666"}20`, color: ROLE_COLORS[other.role] || "#666" }}>
                  {(other.name || "?")[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm truncate">{other.name?.split(" ")[0] || other.email}</span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {c.unread_count > 0 && (
                        <span className="w-5 h-5 rounded-full bg-[#f7c935] text-[#14101c] text-xs font-bold flex items-center justify-center">{c.unread_count}</span>
                      )}
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => deleteConversation(c.conversation_id, e)} data-testid={`delete-convo-${c.conversation_id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-400 hover:text-red-300" />
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 truncate">{c.last_message || "Pas encore de message"}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Chat area */}
      <div className={`${activeConvo ? "flex" : "hidden md:flex"} flex-col flex-1 bg-[#0e0a16] h-full`}>
        {!activeConvo ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 text-slate-700" />
              <p className="text-slate-500">Selectionnez une conversation</p>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-[#161020]">
              <button onClick={() => setActiveConvo(null)} className="md:hidden p-1 rounded hover:bg-white/10">
                <ArrowLeft className="w-5 h-5" />
              </button>
              {(() => {
                const other = getOtherFull(activeConvo);
                return (
                  <>
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: `${ROLE_COLORS[other.role] || "#666"}20`, color: ROLE_COLORS[other.role] || "#666" }}>
                      {(other.name || "?")[0]?.toUpperCase()}
                    </div>
                    <div>
                      <div className="font-semibold text-sm">{other.name || other.email}</div>
                      <span className="text-xs px-1.5 py-0.5 rounded font-medium" style={{ background: `${ROLE_COLORS[other.role] || "#666"}15`, color: ROLE_COLORS[other.role] || "#666" }}>
                        {ROLE_LABELS[other.role] || other.role}
                      </span>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.length === 0 && (
                <div className="text-center py-12 text-slate-500 text-sm">Aucun message. Envoyez le premier !</div>
              )}
              {messages.map(msg => {
                const isMe = msg.sender_id === user?.user_id;
                const canDelete = isMe || isStaff;
                return (
                  <div key={msg.message_id} className={`flex ${isMe ? "justify-end" : "justify-start"} group`} data-testid={`msg-${msg.message_id}`}>
                    <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 relative ${isMe ? "bg-[#f7c935] text-[#14101c] rounded-br-md" : "bg-[#14101c] border border-white/10 text-white rounded-bl-md"}`}>
                      {!isMe && <div className="text-xs font-medium mb-1" style={{ color: "#f7c935" }}>{msg.sender_name?.split(" ")[0]}</div>}
                      {(msg.attachments || []).length > 0 && <MessageAttachments items={msg.attachments} isMe={isMe} />}
                      {msg.content && <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>}
                      <div className={`flex items-center gap-2 mt-1 ${isMe ? "text-[#14101c]/60" : "text-slate-500"}`}>
                        <span className="text-[10px]">{new Date(msg.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                        {msg.pending_sync && <span className="text-[10px] inline-flex items-center gap-0.5" data-testid={`msg-pending-${msg.message_id}`}><Clock className="w-3 h-3" /> En attente</span>}
                        {canDelete && (
                          <button onClick={() => deleteMessage(activeConvo.conversation_id, msg.message_id)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-400" data-testid={`delete-msg-${msg.message_id}`}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="px-4 py-3 pb-5 border-t border-white/10 bg-[#161020]">
              {files.length > 0 && (
                <div className="flex gap-2 flex-wrap mb-2" data-testid="pending-attachments">
                  {files.map((f, i) => (
                    <div key={`${f.name}-${i}`} className="flex items-center gap-1.5 bg-[#0b0811] border border-white/10 rounded-lg px-2 py-1 text-[11px] text-slate-200 max-w-[220px]" data-testid={`pending-attachment-${i}`}>
                      {f.type.startsWith("image/") ? <ImageIcon className="w-3.5 h-3.5 text-[#f7c935]" /> : f.type.startsWith("video/") ? <Film className="w-3.5 h-3.5 text-[#f7c935]" /> : <FileText className="w-3.5 h-3.5 text-[#f7c935]" />}
                      <span className="truncate">{f.name}</span>
                      <span className="text-slate-500">{formatSize(f.size)}</span>
                      {!sending && <button onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))} className="text-slate-400 hover:text-white" data-testid={`remove-attachment-${i}`}><X className="w-3 h-3" /></button>}
                    </div>
                  ))}
                </div>
              )}
              {upload && (
                <div className="mb-2" data-testid="upload-progress">
                  <div className="flex justify-between text-[10px] text-slate-400 mb-1"><span className="truncate">Envoi {upload.index}/{upload.total} · {upload.name}</span><span>{upload.progress}%</span></div>
                  <div className="h-1 rounded bg-white/10 overflow-hidden"><div className="h-full bg-[#f7c935] transition-[width]" style={{ width: `${upload.progress}%` }} /></div>
                </div>
              )}
              <div className="flex gap-2">
                <input ref={fileRef} type="file" accept={ACCEPT} multiple className="hidden" onChange={pickFiles} data-testid="attachment-input" />
                <Button type="button" variant="ghost" onClick={() => fileRef.current?.click()} disabled={sending} className="text-slate-300 hover:text-white px-3" title="Photo, vidéo ou document" data-testid="attach-btn">
                  <Paperclip className="w-4 h-4" />
                </Button>
                <Input value={newMsg} onChange={e => setNewMsg(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()}
                  placeholder={files.length ? "Ajouter un commentaire (optionnel)..." : "Votre message..."} data-testid="message-input"
                  className="bg-[#0b0811] border-white/10 text-white placeholder:text-slate-500 focus:border-[#f7c935]" />
                <Button onClick={sendMessage} disabled={sending || (!newMsg.trim() && files.length === 0)} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] px-4" data-testid="send-btn">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* New Conversation Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-[#161020] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Nouvelle conversation</DialogTitle>
          </DialogHeader>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input placeholder="Rechercher un contact..." value={contactSearch} onChange={e => setContactSearch(e.target.value)}
              className="pl-10 bg-[#0b0811] border-white/10 text-white placeholder:text-slate-500 focus:border-[#f7c935]" data-testid="contact-search" />
          </div>
          <div className="max-h-80 overflow-y-auto space-y-1">
            {filteredContacts.map(c => (
              <button key={c.user_id} onClick={() => startConvo(c.user_id)}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 text-left transition-colors"
                data-testid={`contact-${c.user_id}`}>
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: `${ROLE_COLORS[c.role] || "#666"}20`, color: ROLE_COLORS[c.role] || "#666" }}>
                  {(c.name || "?")[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{c.name || c.email}</span>
                    <span className="text-xs px-1.5 py-0.5 rounded font-medium" style={{ background: `${ROLE_COLORS[c.role] || "#666"}15`, color: ROLE_COLORS[c.role] || "#666" }}>
                      {ROLE_LABELS[c.role] || c.role}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500">{c.email}</div>
                </div>
              </button>
            ))}
            {filteredContacts.length === 0 && (
              <p className="text-center text-slate-500 py-4 text-sm">Aucun contact trouve</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MessageAttachments({ items, isMe }) {
  return (
    <div className="space-y-2 mb-1.5" data-testid="message-attachments">
      {items.map((a) => {
        const url = attachmentUrl(a.attachment_id);
        if (a.kind === "image") {
          return <a key={a.attachment_id} href={url} target="_blank" rel="noreferrer" data-testid={`attachment-image-${a.attachment_id}`}><img src={url} alt={a.filename} loading="lazy" className="rounded-xl max-h-64 max-w-full object-cover" /></a>;
        }
        if (a.kind === "video") {
          return <video key={a.attachment_id} src={url} controls preload="metadata" playsInline className="rounded-xl max-h-64 max-w-full bg-black" data-testid={`attachment-video-${a.attachment_id}`} />;
        }
        if (a.kind === "audio") {
          return <audio key={a.attachment_id} src={url} controls preload="metadata" className="max-w-full" data-testid={`attachment-audio-${a.attachment_id}`} />;
        }
        return (
          <a key={a.attachment_id} href={url} target="_blank" rel="noreferrer" className={`flex items-center gap-2 rounded-lg px-2.5 py-2 text-xs ${isMe ? "bg-[#14101c]/10" : "bg-white/5"}`} data-testid={`attachment-doc-${a.attachment_id}`}>
            <FileText className="w-4 h-4 flex-shrink-0" />
            <span className="truncate flex-1 font-medium">{a.filename}</span>
            <span className="opacity-70">{formatSize(a.size)}</span>
            <Download className="w-3.5 h-3.5 flex-shrink-0" />
          </a>
        );
      })}
    </div>
  );
}
